import React, { useEffect, useState } from 'react';
import { useTranslator } from '@eo-locale/react';
import InputAdornment from '@mui/material/InputAdornment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CancelIcon from '@mui/icons-material/Cancel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ListSubheader from '@mui/material/ListSubheader';
import { styled } from '@mui/material/styles';
import { useId } from '@guest-widgets/shared/src/hooks/useId';
import { ids } from '@guest-widgets/shared/src/ids';
import { classes } from '@guest-widgets/shared/src/classes';
import { WithClassName } from '@guest-widgets/shared/src/types/withClassName';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  LocationSuggestionsDto,
  SuggestedLocationDto,
} from '@checkfront/guest-experience-api-api-client-javascript-axios';

import { defaultRadius, Location, locationsAreEqual } from '../../../filters';
import { useFilters } from '../../../filtersContext/filtersContext';

import { useLocationSuggestions } from './useLocationSuggestions';

export const LocationFilter = ({ className }: WithClassName) => {
  const translator = useTranslator();
  const {
    filters: { location },
    setLocation,
  } = useFilters();
  const id = useId(ids.catalog.locationFilter);
  const [value, setValue] = useState<AutocompleteOption | null>(
    locationToAutocompleteOption(location)
  );
  const [keyword, setKeyword] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const suggestions = useLocationSuggestions(keyword);
  const options = suggestions.value ? mapToAutocompleteOptions(suggestions.value) : [];

  useEffect(() => {
    const newLocation = autocompleteOptionToLocation(value, location?.radius || defaultRadius);

    if (locationsAreEqual(newLocation, location)) return;

    setLocation(newLocation);
  }, [value]);

  useEffect(() => {
    if (!location) {
      setValue(null);
      return setKeyword('');
    }

    if (location.latitude !== value?.latitude || location.longitude !== value?.longitude)
      setValue(locationToAutocompleteOption(location));
  }, [location]);

  const suggestionsClassName = `${classes.catalog.filters.popup.root} ${classes.catalog.filters.location.suggestions}`;

  return (
    <>
      <Autocomplete
        classes={{ popper: suggestionsClassName }}
        className={`${classes.catalog.filters.location.input} ${className || ''}`}
        id={id}
        fullWidth
        size="small"
        open={isOpen && keyword.length > 2}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        getOptionLabel={(option) => option.name}
        options={options}
        groupBy={(option) =>
          translator.translate(`product-catalog.filters.location.groups.${option.group}`)
        }
        value={value}
        onChange={(event, newValue) => setValue(newValue)}
        onInputChange={(event, newKeyword) => setKeyword(newKeyword)}
        loading={suggestions.isLoading}
        noOptionsText={translator.translate('product-catalog.filters.location.no-options')}
        loadingText={translator.translate('product-catalog.filters.location.loading')}
        autoComplete
        autoHighlight
        clearIcon={
          <CancelIcon
            sx={{ visibility: 'visible', mt: -1 }}
            data-cy="location-filter-close-button"
          />
        }
        forcePopupIcon={isOpen && keyword.length > 2}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            color="secondary"
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <CenteredLocationIcon />
                </InputAdornment>
              ),
            }}
            placeholder={translator.translate('product-catalog.filters.location.placeholder')}
            label={translator.translate('product-catalog.filters.location.title')}
          />
        )}
        renderOption={(props, option) => {
          const matches = match(option.name, keyword);
          const parts = parse(option.name, matches);
          return (
            <li {...props}>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
            </li>
          );
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>
              <GroupLabelOuter>
                <GroupLabel>{params.group}</GroupLabel>
              </GroupLabelOuter>
            </GroupHeader>
            <GroupList>{params.children}</GroupList>
          </li>
        )}
      />
    </>
  );
};

type AutocompleteOption = Omit<Location, 'radius'> & { group: string };

const mapToAutocompleteOptions = ({
  cities,
  regions,
  holidayRegions,
}: LocationSuggestionsDto): AutocompleteOption[] => [
  ...cities.map(mapToAutocompleteOption('cities')),
  ...regions.map(mapToAutocompleteOption('regions')),
  ...holidayRegions.map(mapToAutocompleteOption('holiday-regions')),
];

const mapToAutocompleteOption = (group: string) => ({
  name,
  geolocation: { longitude, latitude },
}: SuggestedLocationDto): AutocompleteOption => ({
  name,
  longitude,
  latitude,
  group,
});

const locationToAutocompleteOption = (
  location: Location | undefined
): AutocompleteOption | null => {
  if (!location) return null;
  const { radius, ...rest } = location;
  return { ...rest, group: '' };
};

const autocompleteOptionToLocation = (
  option: AutocompleteOption | null,
  radius: number
): Location | undefined => {
  if (!option) return undefined;
  const { group, ...rest } = option;
  return { ...rest, radius };
};

const CenteredLocationIcon = styled(LocationOnIcon)(({ theme: { spacing } }) => ({
  marginLeft: 8,
  marginRight: -8,
}));

const GroupHeader = styled((props) => <ListSubheader {...props} component="div" />)(
  ({ theme: { palette, spacing, typography } }) => ({
    color: palette.text.primary,
    fontSize: typography.h6.fontSize,
    textTransform: 'uppercase',
    textAlign: 'center',
    backgroundColor: palette.background.default,
    top: spacing(-2),
    padding: spacing(3, 0),
  })
);

const GroupLabelOuter = styled('div')(({ theme: { border } }) => ({
  borderBottom: border,
  lineHeight: '0.1em',
}));

const GroupLabel = styled('span')(({ theme: { palette, spacing } }) => ({
  background: palette.background.default,
  padding: spacing(0, 2),
  position: 'relative',
  top: spacing(0.5),
}));

const GroupList = styled('ul')(({ theme: { palette, spacing } }) => ({
  backgroundColor: palette.background.default,
  padding: 0,
  '& > li': {
    paddingLeft: spacing(8),
  },
}));
